/**
 * Shared parameter parsing utilities for Office Connector macros
 * (viewxls, viewpdf, etc.).
 *
 * These utilities implement the same logic as the legacy ConverterMacro.java
 * backend and are shared across all office file macro packages.
 */

export type DecodedPageParams = {
	pageName: string | null;
	spaceKey: string | null;
	date: string | null;
};

/**
 * Normalises a size string from macro params.
 * Matches the legacy ConverterMacro.convertToSizeParam() behaviour:
 *   - Ends with "%"  -> strip non-numeric chars except decimal points, append "%"
 *   - Otherwise      -> strip non-numeric chars except decimal points, append "px"
 *
 * If stripping leaves no numeric content (e.g. input is "abc" or "em"),
 * the original string is returned unchanged to avoid producing invalid CSS
 * values like "px" or "%".
 */
export const normalizeSizeParam = (size: string): string => {
	const numericSize = size.replace(/[^\d.]+/g, '');

	if (!numericSize) {
		return size;
	}

	if (size.endsWith('%')) {
		return `${numericSize}%`;
	}

	return `${numericSize}px`;
};

export const computeViewerDimensions = (
	rawWidth: string | undefined,
	rawHeight: string | undefined,
	defaultWidth: string,
	defaultHeight: string,
): { width: string; height: string } => ({
	width: rawWidth ? normalizeSizeParam(rawWidth) : defaultWidth,
	height: rawHeight ? normalizeSizeParam(rawHeight) : defaultHeight,
});

/**
 * Compatibility parser for legacy Office Connector page param encodings
 * like "SPACEKEY:Page Title" and "SPACEKEY:/yyyy/mm/dd/Page Title".
 */
export const decodeLegacyMacroPageParam = (rawPageParam: string | null): DecodedPageParams => {
	if (!rawPageParam) {
		return { pageName: null, spaceKey: null, date: null };
	}

	const pageParam = rawPageParam.trim();
	if (!pageParam) {
		return { pageName: null, spaceKey: null, date: null };
	}

	const firstColon = pageParam.indexOf(':');
	const spacePart = firstColon !== -1 ? pageParam.slice(0, firstColon) || null : null;
	const titlePart = firstColon !== -1 ? pageParam.slice(firstColon + 1) : pageParam;

	let pageName = titlePart;
	let date: string | null = null;

	const datePathMatch = titlePart.match(/^\/(\d{4})\/(\d{2})\/(\d{2})\/(.+)$/);
	if (datePathMatch) {
		const [, year, month, day, title] = datePathMatch;
		date = `${month}/${day}/${year}`;
		pageName = title;
	}

	return {
		pageName: pageName || null,
		spaceKey: spacePart || null,
		date,
	};
};

/**
 * Converts a date string from mm/dd/yyyy to yyyy-mm-dd format
 * as expected by the AGG `postingDay` argument.
 */
export const formatPostingDay = (date: string): string | undefined => {
	const parts = date.split('/');
	if (parts.length !== 3) {
		return undefined;
	}
	const [month, day, year] = parts;
	return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

export type BuildOfficeAttachmentQueryVariablesOptions = {
	contentId: string | null | undefined;
	pageName?: string | null;
	spaceKey?: string | null;
	date?: string | null;
};

export type OfficeAttachmentQueryVariables = {
	contentId?: string;
	contentStatus?: ReadonlyArray<string | null> | null;
	spaceKey?: string;
	title?: string;
	type?: string;
	postingDay?: string;
};

/**
 * Builds GraphQL query variables for the office attachment query
 * (used by viewxls, viewpdf, and other Office Connector macros).
 * Shared across all office file macro packages.
 */
export const buildOfficeAttachmentQueryVariables = ({
	contentId,
	pageName,
	spaceKey,
	date,
}: BuildOfficeAttachmentQueryVariablesOptions): OfficeAttachmentQueryVariables => {
	const normalizedPageName = pageName?.trim() || null;
	if (normalizedPageName) {
		const formattedDay = date ? formatPostingDay(date) : undefined;
		const variables: OfficeAttachmentQueryVariables = {
			contentStatus: ['current'],
			title: normalizedPageName,
			type: formattedDay ? 'blogpost' : 'page',
		};
		if (spaceKey) {
			variables.spaceKey = spaceKey;
		}
		if (formattedDay) {
			variables.postingDay = formattedDay;
		}
		return variables;
	}
	return {
		contentId: contentId ?? undefined,
		contentStatus: ['current'],
	};
};
